/* eslint-disable indent */
/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import { flip, offset, shift, useFloating } from '@floating-ui/react-dom'
import { useCurrentMember } from '@globals/useCurrentMember'
import {
  ListTenantMembersParams,
  PageResult,
  TenantMember,
  listTenantMembers,
} from '@kudos/http-client'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  LexicalTypeaheadMenuPlugin,
  MenuTextMatch,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin'
import { fullNameFromParts } from '@shared/utils/nameFormatting'
import { TextNode } from 'lexical'
import { isNull } from 'lodash/fp'
import { useCallback, useEffect, useMemo, useState } from 'react'
import * as ReactDOM from 'react-dom'
import { $createMentionNode } from './MentionNode'
import MentionsTypeaheadList from './MentionsTypeaheadList'
import { MentionTypeaheadOption } from './MentionsTypeaheadMenuItem'

const PUNCTUATION = '\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%\'"~=<>_:;'
const NAME = `\\b[A-Z][^\\s${PUNCTUATION}]`

const DocumentMentionsRegex = {
  NAME,
  PUNCTUATION,
}

const PUNC = DocumentMentionsRegex.PUNCTUATION

const TRIGGERS = ['@'].join('')

// Chars we expect to see in a mention (non-space, non-punctuation).
const VALID_CHARS = `[^${TRIGGERS}${PUNC}\\s]`

// Non-standard series of chars. Each series must be preceded and followed by
// a valid char.
const VALID_JOINS = `(?:\\.[ |$]| |[${PUNC}]|)`

const LENGTH_LIMIT = 75

const AtSignMentionsRegex = new RegExp(
  `(^|\\s|\\()([${TRIGGERS}]((?:${VALID_CHARS}${VALID_JOINS}){0,${LENGTH_LIMIT}}))$`,
)

// 50 is the longest alias length limit.
const ALIAS_LENGTH_LIMIT = 50

// Regex used to match alias.
const AtSignMentionsRegexAliasRegex = new RegExp(
  `(^|\\s|\\()([${TRIGGERS}]((?:${VALID_CHARS}){0,${ALIAS_LENGTH_LIMIT}}))$`,
)

// At most, 5 suggestions are shown in the popup.
const SUGGESTION_LIST_LENGTH_LIMIT = 5

const mentionsCache = new Map<string | null, Array<TenantMember> | null>()

function useMentionLookupService(mentionString: string | null) {
  const [results, setResults] = useState<Array<TenantMember>>([])

  const { member: tenantMember } = useCurrentMember()

  const tenantMemberId = tenantMember?.id

  useEffect(() => {
    const cachedResults = mentionsCache.get(mentionString)

    if (isNull(mentionString)) {
      setResults([])
      return
    }

    if (isNull(cachedResults)) {
      return
    } else if (cachedResults !== undefined) {
      setResults(cachedResults)
      return
    }

    mentionsCache.set(mentionString, null)

    // search function starts here
    let params: ListTenantMembersParams = {
      notIdIn: [tenantMemberId!],
      suspended: false,
      page: 1,
      pageSize: 10,
      embeds: ['avatar'],
    }

    if (mentionString) {
      params = { ...params, search: mentionString }
    }

    listTenantMembers(params).then((response: PageResult<TenantMember>) => {
      mentionsCache.set(mentionString, response?.results)
      setResults(response?.results)
    })
  }, [mentionString])

  return results
}

function checkForAtSignMentions(text: string, minMatchLength: number): MenuTextMatch | null {
  let match = AtSignMentionsRegex.exec(text)

  if (isNull(match)) {
    match = AtSignMentionsRegexAliasRegex.exec(text)
  }
  if (match !== null) {
    // The strategy ignores leading whitespace but we need to know it's
    // length to add it to the leadOffset
    const maybeLeadingWhitespace = match[1]

    const matchingString = match[3]
    if (matchingString.length >= minMatchLength) {
      return {
        leadOffset: match.index + maybeLeadingWhitespace.length,
        matchingString,
        replaceableString: match[2],
      }
    }
  }
  return null
}

function getPossibleQueryMatch(text: string): MenuTextMatch | null {
  return checkForAtSignMentions(text, 1)
}

type MentionsPluginProps = {
  setAreMentionsOpen: (isOpen: boolean) => void
}

const MentionsPlugin: React.FC<MentionsPluginProps> = ({
  setAreMentionsOpen,
}): JSX.Element | null => {
  const [editor] = useLexicalComposerContext()

  const [queryString, setQueryString] = useState<string | null>(null)

  const results = useMentionLookupService(queryString)

  const checkForSlashTriggerMatch = useBasicTypeaheadTriggerMatch('/', {
    minLength: 0,
  })

  const options = useMemo(
    () =>
      results
        .map(
          result =>
            new MentionTypeaheadOption(
              result.id,
              result.firstName,
              result.lastName,
              result.avatarUrls,
            ),
        )
        .slice(0, SUGGESTION_LIST_LENGTH_LIMIT),
    [results],
  )

  const onSelectOption = useCallback(
    (
      selectedOption: MentionTypeaheadOption,
      nodeToReplace: TextNode | null,
      closeMenu: () => void,
    ) => {
      editor.update(() => {
        const mentionNode = $createMentionNode({
          id: selectedOption.id,
          name: fullNameFromParts(selectedOption.firstName, selectedOption.lastName),
        })

        if (nodeToReplace) {
          nodeToReplace.replace(mentionNode)
        }
        mentionNode.select()
        closeMenu()
      })
    },
    [editor],
  )

  const checkForMentionMatch = useCallback(
    (text: string) => {
      const slashMatch = checkForSlashTriggerMatch(text, editor)
      if (slashMatch !== null) {
        return null
      }
      return getPossibleQueryMatch(text)
    },
    [checkForSlashTriggerMatch, editor],
  )

  // NOTE: currently, the provided plugin code from Meta/Lexical
  // doesn't handle viewport checks, resulting in the typeahead menu
  // being rendered off-screen when the cursor is close to the right
  // side. This library handles those checks and fixes the render, keeping
  // everything safely within the viewport.
  // See: https://github.com/facebook/lexical/issues/3834
  const { refs } = useFloating({
    placement: 'bottom-start',
    middleware: [offset(6), flip(), shift()],
  })

  return (
    <LexicalTypeaheadMenuPlugin<MentionTypeaheadOption>
      onQueryChange={setQueryString}
      onSelectOption={onSelectOption}
      onOpen={r => {
        refs.setReference({
          getBoundingClientRect: r.getRect,
        })
      }}
      triggerFn={checkForMentionMatch}
      options={options}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex },
      ) =>
        anchorElementRef.current && results.length
          ? ReactDOM.createPortal(
              <MentionsTypeaheadList
                setAreMentionsOpen={setAreMentionsOpen}
                options={options}
                selectedIndex={selectedIndex}
                onClick={(i, option) => {
                  setHighlightedIndex(i)
                  selectOptionAndCleanUp(option)
                }}
                onMouseEnter={i => {
                  setHighlightedIndex(i)
                }}
              />,
              anchorElementRef.current,
            )
          : null
      }
    />
  )
}

export default MentionsPlugin
